export interface SlugPanelConfig {
  panels?: string[];
  order?: string[];
  headline?: string; // can include {Subject} and {Type}
  tagline?: string; // optional tagline for first panel (no location) - supports {Subject}, {Type}
  taglineWithLocation?: string; // optional tagline for first panel (with location) - supports {Subject}, {Type}, {Location}
  firstQuestion?: string; // explicit override for intro panel question
  assumedGradeLevel?: string; // optional static grade level assumption (e.g., 'High School')
}

export interface SlugConfigurations {
  configurations: Record<string, SlugPanelConfig>;
  default: SlugPanelConfig;
}

// Label override mappings for acronyms and proper casing
export const subjectLabels: Record<string, string> = {
  '3d_animation': '3D Animation',
  sat: 'SAT',
  sat_math: 'SAT Math',
  act: 'ACT',
  act_english: 'ACT English',
  act_math: 'ACT Math',
  act_reading: 'ACT Reading',
  act_science: 'ACT Science',
  act_writing: 'ACT Writing',
  gre: 'GRE',
  gmat: 'GMAT',
  lsat: 'LSAT',
  mcat: 'MCAT',
  dat: 'DAT',
  oat: 'OAT',
  pcat: 'PCAT',
  teas: 'TEAS',
  ap: 'AP',
  add: 'ADD',
  adhd: 'ADHD',
  ap_art_history: 'AP Art History',
  ap_biology: 'AP Biology',
  ap_calculus: 'AP Calculus',
  ap_calculus_ab: 'AP Calculus AB',
  ap_calculus_bc: 'AP Calculus BC',
  ap_chemistry: 'AP Chemistry',
  ap_chinese_language_and_culture: 'AP Chinese Language & Culture',
  ap_comparative_government_and_politics: 'AP Comparative Government & Politics',
  ap_computer_science_a: 'AP Computer Science A',
  ap_computer_science_principles: 'AP Computer Science Principles',
  ap_drawing: 'AP Drawing',
  ap_economics: 'AP Economics',
  ap_english_language_and_composition: 'AP English Language & Composition',
  ap_environmental_science: 'AP Environmental Science',
  ap_european_history: 'AP European History',
  ap_french: 'AP French',
  ap_french_language_and_culture: 'AP French Language and Culture',
  ap_geography: 'AP Geography',
  ap_german_language_and_culture: 'AP German Language and Culture',
  ap_human_geography: 'AP Human Geography',
  ap_italian_language_and_culture: 'AP Italian Language and Culture',
  ap_japanese_language_and_culture: 'AP Japanese Language and Culture',
  ap_latin: 'AP Latin',
  ap_macroeconomics: 'AP Macroeconomics',
  ap_microeconomics: 'AP Microeconomics',
  ap_physics: 'AP Physics',
  ap_physics_1: 'AP Physics 1',
  ap_physics_2: 'AP Physics 2',
  ap_physics_b: 'AP Physics B',
  ap_physics_c_electricity_and_magnetism: 'AP Physics C - Electricity & Magnetism',
  ap_physics_c_mechanics: 'AP Physics C - Mechanics',
  ap_research: 'AP Research',
  ap_seminar: 'AP Seminar',
  ap_studio_art_2_d_design: 'AP Studio Art',
  ap_studio_art_drawing: 'AP Studio Art Drawing',
  ap_u_s_government_politics: 'AP Government Politics',
  ap_united_states_history: 'AP US History',
  ap_world_history: 'AP World History',
  ib: 'IB',
  psat: 'PSAT',
  ssat: 'SSAT',
  isee: 'ISEE',
  hspt: 'HSPT',
  tachs: 'TACHS',
  shsat: 'SHSAT',
  differential_equations: 'Differential Equations',
  linear_algebra: 'Linear Algebra',
  organic_chemistry: 'Organic Chemistry',
  cpa: 'CPA',
  cma: 'CMA',
  cfa: 'CFA',
  caia: 'CAIA',
  fe: 'FE',
  nclex: 'NCLEX',
  nclex_rn: 'NCLEX-RN',
  nclex_pn: 'NCLEX-PN',
  nclex_pa: 'NCLEX-PA',
  nclex_lp: 'NCLEX-LP',
  usmle: 'USMLE',
  usmle_step_1: 'USMLE Step 1',
  usmle_step_2: 'USMLE Step 2',
  usmle_step_3: 'USMLE Step 3',
  afoqt: 'AFOQT',
  ielts: 'IELTS',
  toefl: 'TOEFL',
  abim: 'ABIM',
  ancc: 'ANCC',
  acp: 'ACP',
  acsm: 'ACSM',
  ardms: 'ARDMS',
  astb: 'ASTB',
  aws: 'AWS',
  aws_certified_cloud_practitioner: 'AWS Certified Cloud Practitioner',
  bcaba: 'BCABA',
  cbest: 'CBEST',
  ccna: 'CCNA',
  ccna_routing_and_switching: 'CCNA Routing and Switching',
  cfa_level_1: 'CFA Level 1',
  cfa_level_2: 'CFA Level 2',
  cissp: 'CISSP',
  cna: 'CNA',
  comlex: 'COMLEX',
  comptia: 'CompTIA',
  cpc: 'CPC',
  cscp: 'CSCP',
  eas: 'EAS',
  eit: 'EIT',
  exam_fm: 'Exam FM',
  faa: 'FAA',
  ftce: 'FTCE',
  gace: 'GACE',
  ibew: 'IBEW',
  lsw: 'LSW',
  mpje: 'MPJE',
  nasm: 'NASM',
  nbcot: 'NBCOT',
  nbde: 'NBDE',
  nbe: 'NBE',
  oae: 'OAE',
  oar: 'OAR',
  pe_exam: 'PE Exam',
  ptce: 'PTCE',
  shrm_cp: 'SHRM-CP',
  vtne: 'VTNE',
  arrt: 'ARRT',
  barexam: 'Bar Exam',
  cfp: 'CFP',
  ea: 'EA',
  emt: 'EMT',
  eppp: 'EPPP',
  lmsw: 'LSMW',
  mblex: 'MBLEX',
  naplex: 'NAPLEX',
  nmls: 'NMLS',
  npte: 'NPTE',
  pance: 'PANCE',
  pmp: 'PMP',
  praxis: 'PRAXIS',
  sie: 'SIE',
  ube: 'UBE',
  comptia_a_exam: 'CompTIA A+ Exam',
  comptia_network_exam: 'CompTIA Network+ Exam',
  comptia_security_exam: 'CompTIA Security+ Exam',
};

export const typeLabels: Record<string, string> = {
  tutoring: 'Tutoring',
  tutor: 'Tutoring',
  courses: 'Courses',
  course: 'Courses',
  classes: 'Classes',
  class: 'Classes',
  'test-prep': 'Test Prep',
  testprep: 'Test Prep',
  prep: 'Prep',
  nt: 'Prep',
};

export function getSubjectLabel(key: string | null | undefined): string {
  if (!key) return '';
  // Treat encoded spaces (%20) the same as underscores to match subject label keys
  const normalizedKey = key.replace(/%20/gi, '_');
  const lower = normalizedKey.toLowerCase();
  return subjectLabels[lower] || key
    .replace(/[\-_]+/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());
}

export function getTypeLabel(key: string | null | undefined): string {
  if (!key) return '';
  const lower = key.toLowerCase();
  return typeLabels[lower] || key
    .replace(/[\-_]+/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());
}

export function formatHeadline(template: string, vars: { subject?: string; type?: string; location?: string }): string {
  return template
    .replace('{Subject}', vars.subject ?? '')
    .replace('{Type}', vars.type ?? '')
    .replace('{Location}', vars.location ?? '');
}

// Canonical default standard order based on form-config.ts
// Note: Using conditional grade panels for better personalization
const FULL_OPTION_GRADE_PANELS = [
  'who-needs-help-reg',
  'grade-level-cond-self',    // Shows for "I do"
  'grade-level-cond-child',   // Shows for "My child"
  'grade-level-cond-other',   // Shows for "Someone else"
  'adult-learning-goal',      // Shows for "I do" + Adult Learner
];

const US_CONTACT_TAIL = [
  'how_soon-reg',
  'zipcode-reg',
  'school-district-reg',
  'partner-school-reg',
  /*'finding-tutor-reg',*/
  'full-name-reg',
  'email-reg',
  'phone-reg',
];

const FULL_OPTION_FLOW = [
  ...FULL_OPTION_GRADE_PANELS,
  'learning-differences',
  ...US_CONTACT_TAIL,
];

const FULL_OPTION_FLOW_WITHOUT_IEP = [
  ...FULL_OPTION_GRADE_PANELS,
  ...US_CONTACT_TAIL,
];

// Elementary pages: a parent or another adult fills in the form, so "Who needs tutoring?" leaves out "I do".
const ELEMENTARY_K6_CONFIG: SlugPanelConfig = {
  panels: ['who-needs-help-young', 'learning-differences-child', 'how_soon-reg', 'zipcode-reg', 'school-district-reg', 'partner-school-reg', /*'finding-tutor-reg',*/ 'full-name-reg', 'email-reg', 'phone-reg'],
  assumedGradeLevel: 'Elementary school',
};

// Grad test prep asks about exam timing instead of who needs help / grade level.
const GRAD_TEST_PREP_CONFIG: SlugPanelConfig = {
  panels: ['when-taking', 'taken-before',  'zipcode-reg', /*'finding-tutor-reg',*/ 'full-name-reg', 'email-reg', 'phone-reg'],
};

// The exam pages know the test from the URL, so their questions name it ("When are you taking
// the LSAT?"). The campaign rule above may have no subject, so it keeps "the exam".
const GRAD_TEST_PREP_NAMED_CONFIG: SlugPanelConfig = {
  panels: ['when-taking-subject', 'taken-before-subject', 'zipcode-reg', /*'finding-tutor-reg',*/ 'full-name-reg', 'email-reg', 'phone-reg'],
};

// Matched by subject token so every campaign (tutor and non-tutor) for these exams
// gets the grad flow regardless of campaign ID.
// TEAS is a nursing school entrance exam, so it asks about exam timing like the grad exams.
const GRAD_TEST_PREP_SUBJECTS = ['lsat', 'mcat', 'gmat', 'gre', 'dat', 'oat', 'pcat', 'teas'];

const CONTACT_TAIL = ['zipcode-reg', /*'finding-tutor-reg',*/ 'full-name-reg', 'email-reg', 'phone-reg'];

// The page names the student's level, so these flows skip the grade question and assume it.
// College and adult students aren't asked for a school district.
const COLLEGE_CONFIG: SlugPanelConfig = {
  panels: ['who-needs-help-reg', 'how_soon-reg', ...CONTACT_TAIL],
  assumedGradeLevel: 'College or Graduate',
};

const ADULT_CONFIG: SlugPanelConfig = {
  panels: ['adult-learning-goal-direct', 'how_soon-reg', ...CONTACT_TAIL],
  assumedGradeLevel: 'Adult Learner',
};

// Private school and specialized high school entrance exams are taken by middle schoolers.
const ENTRANCE_EXAM_SUBJECTS = ['hspt', 'isee', 'ssat', 'tachs', 'shsat'];

const entranceExamConfig = (subject: string): SlugPanelConfig => ({
  panels: ['who-needs-help-alt', 'how_soon-reg', 'zipcode-reg', 'school-district-reg', 'partner-school-reg', /*'finding-tutor-reg',*/ 'full-name-reg', 'email-reg', 'phone-reg'],
  firstQuestion: `Who will be taking the ${getSubjectLabel(subject)}?`,
  assumedGradeLevel: 'Middle school',
});

// Kindergarten to 5th grade pages already give the grade, so they get the elementary flow.
const YOUNG_GRADE_SLUG = /kindergarten|phonics|primary_school|grade_school|(^|[-_])[1-5](st|nd|rd|th)_grade/;

// The copy guide doesn't use "kids", so the Kids pages ask "Who needs tutoring?" rather than
// "Who needs Kids Tutoring?".
const KIDS_CONFIG: SlugPanelConfig = {
  ...ELEMENTARY_K6_CONFIG,
  firstQuestion: 'Who needs tutoring?',
};

const isElementaryOrKidsSlug = (slugKey: string): boolean => {
  const normalizedKey = (slugKey || '').toLowerCase();
  return normalizedKey.includes('elementary_school') || normalizedKey.includes('kids') || YOUNG_GRADE_SLUG.test(normalizedKey);
};

export const slugFormConfigs: SlugConfigurations = {
  configurations: {
    // Keep the generic national ordering configurable here while matching standard order by default
    'tutoring-national': {
      panels: [...FULL_OPTION_FLOW],
      headline: 'Tutoring for Students Everywhere',
    },
  'national-math-tutoring': {
      panels: [...FULL_OPTION_FLOW],
    },
    'national-act-classes': {
      panels: ['who-needs-help-alt', 'how_soon-reg', 'zipcode-reg', 'school-district-reg', 'partner-school-reg', /*'finding-tutor-reg',*/ 'full-name-reg', 'email-reg', 'phone-reg'],
      headline: 'ACT Classes',
      firstQuestion: 'Who will be taking the ACT?',
      assumedGradeLevel: 'High School',
    },
    'national-act-courses': {
      panels: ['who-needs-help-alt', 'how_soon-reg', 'zipcode-reg', 'school-district-reg', 'partner-school-reg', /*'finding-tutor-reg',*/ 'full-name-reg', 'email-reg', 'phone-reg'],
      headline: 'ACT Courses',
      firstQuestion: 'Who will be taking the ACT?',
      assumedGradeLevel: 'High School',
    },
    'national-act-prep': {
      panels: ['who-needs-help-alt', 'how_soon-reg', 'zipcode-reg', 'school-district-reg', 'partner-school-reg', /*'finding-tutor-reg',*/ 'full-name-reg', 'email-reg', 'phone-reg'],
      headline: 'ACT Prep',
      firstQuestion: 'Who will be taking the ACT?',
      assumedGradeLevel: 'High School',
    },
    'national-act-lessons': {
      panels: ['who-needs-help-alt', 'how_soon-reg','zipcode-reg', 'school-district-reg', 'partner-school-reg', /*'finding-tutor-reg',*/ 'full-name-reg', 'email-reg', 'phone-reg'],
      headline: 'ACT Lessons',
      firstQuestion: 'Who will be taking the ACT?',
      assumedGradeLevel: 'High School',
    },
    'national-act-tutoring': {
      panels: ['who-needs-help-reg','how_soon-reg', 'zipcode-reg', 'school-district-reg', 'partner-school-reg', /*'finding-tutor-reg',*/ 'full-name-reg', 'email-reg', 'phone-reg'],
      assumedGradeLevel: 'High School',
    },
    'national-isee-tutoring': entranceExamConfig('isee'),
    'national-physics-tutoring': {
      panels: ['who-needs-help-reg', 'grade-level-hs-col-cond-self', 'grade-level-hs-col-cond-child', 'grade-level-hs-col-cond-other', 'how_soon-reg', 'zipcode-reg', 'school-district-reg', 'partner-school-reg', /*'finding-tutor-reg',*/ 'full-name-reg', 'email-reg', 'phone-reg'],
    },
    'national-chemistry-tutoring': {
      panels: ['who-needs-help-reg', 'grade-level-hs-col-cond-self', 'grade-level-hs-col-cond-child', 'grade-level-hs-col-cond-other', 'how_soon-reg', 'zipcode-reg', 'school-district-reg', 'partner-school-reg', /*'finding-tutor-reg',*/ 'full-name-reg', 'email-reg', 'phone-reg'],
    },
    'national-calculus-tutoring': {
      panels: ['who-needs-help-reg', 'grade-level-hs-col-cond-self', 'grade-level-hs-col-cond-child', 'grade-level-hs-col-cond-other', 'how_soon-reg', 'zipcode-reg', 'school-district-reg', 'partner-school-reg', /*'finding-tutor-reg',*/ 'full-name-reg', 'email-reg', 'phone-reg'],
    },
    'national-biology-tutoring': {
      panels: ['who-needs-help-reg', 'grade-level-hs-col-cond-self', 'grade-level-hs-col-cond-child', 'grade-level-hs-col-cond-other', 'how_soon-reg', 'zipcode-reg', 'school-district-reg', 'partner-school-reg', /*'finding-tutor-reg',*/ 'full-name-reg', 'email-reg', 'phone-reg'],
    },
    'national-algebra-tutoring': {
      panels: ['who-needs-help-reg', 'grade-level-hs-col-cond-self', 'grade-level-hs-col-cond-child', 'grade-level-hs-col-cond-other', 'how_soon-reg', 'zipcode-reg', 'school-district-reg', 'partner-school-reg', /*'finding-tutor-reg',*/ 'full-name-reg', 'email-reg', 'phone-reg'],
    },
    'national-statistics-tutoring': {
      panels: ['who-needs-help-reg', 'grade-level-hs-col-cond-self', 'grade-level-hs-col-cond-child', 'grade-level-hs-col-cond-other', 'how_soon-reg', 'zipcode-reg', 'school-district-reg', 'partner-school-reg', /*'finding-tutor-reg',*/ 'full-name-reg', 'email-reg', 'phone-reg'],
    },
    'national-computer_science-tutoring': {
      panels: ['who-needs-help-reg', 'grade-level-hs-col-cond-self', 'grade-level-hs-col-cond-child', 'grade-level-hs-col-cond-other', 'how_soon-reg', 'zipcode-reg', 'school-district-reg', 'partner-school-reg', /*'finding-tutor-reg',*/ 'full-name-reg', 'email-reg', 'phone-reg'],
    },
    'national-precalculus-tutoring': {
      panels: ['who-needs-help-reg', 'grade-level-hs-col-cond-self', 'grade-level-hs-col-cond-child', 'grade-level-hs-col-cond-other', 'how_soon-reg', 'zipcode-reg', 'school-district-reg', 'partner-school-reg', /*'finding-tutor-reg',*/ 'full-name-reg', 'email-reg', 'phone-reg'],
    },
    'national-geometry-tutoring': {
      panels: ['who-needs-help-reg', 'grade-level-hs-col-cond-self', 'grade-level-hs-col-cond-child', 'grade-level-hs-col-cond-other', 'how_soon-reg', 'zipcode-reg', 'school-district-reg', 'partner-school-reg', /*'finding-tutor-reg',*/ 'full-name-reg', 'email-reg', 'phone-reg'],
    },
    'national-organic_chemistry-tutoring': {
      panels: ['who-needs-help-reg', 'how_soon-reg', 'zipcode-reg', 'school-district-reg', 'partner-school-reg', /*'finding-tutor-reg',*/ 'full-name-reg', 'email-reg', 'phone-reg'],
      assumedGradeLevel: 'College or Graduate',
    },
    'national-biochemistry-tutoring': {
      panels: ['who-needs-help-reg', 'how_soon-reg', 'zipcode-reg', 'school-district-reg', 'partner-school-reg', /*'finding-tutor-reg',*/ 'full-name-reg', 'email-reg', 'phone-reg'],
      assumedGradeLevel: 'College or Graduate',
    },
    'national-spanish-tutoring': {
      panels: [...FULL_OPTION_FLOW_WITHOUT_IEP],
    },
    'national-high_school-tutoring': {
      panels: ['who-needs-help-reg', 'how_soon-reg', 'zipcode-reg', 'school-district-reg', 'partner-school-reg', /*'finding-tutor-reg',*/ 'full-name-reg', 'email-reg', 'phone-reg'],
      assumedGradeLevel: 'High School',
    },
    'national-middle_school-tutoring': {
      panels: ['who-needs-help-reg', 'learning-differences-child', 'how_soon-reg', 'zipcode-reg', 'school-district-reg', 'partner-school-reg', /*'finding-tutor-reg',*/ 'full-name-reg', 'email-reg', 'phone-reg'],
      assumedGradeLevel: 'Middle school',
    },
    'national-ap-tutoring': {
      panels: ['who-needs-help-reg', 'how_soon-reg', 'zipcode-reg', 'school-district-reg', 'partner-school-reg', /*'finding-tutor-reg',*/ 'full-name-reg', 'email-reg', 'phone-reg'],
      assumedGradeLevel: 'High School',
    },

    // ============================================================================
    // TAGLINE TEST EXAMPLES (Uncomment to test)
    // ============================================================================
    // Example 1: Different phrasing for location vs no-location
    // 'national-sat-tutoring-test-a': {
    //   panels: ['who-needs-help-alt', 'sat-section', 'how_soon-reg','zipcode-reg', 'school-district-reg', 'partner-school-reg', 'full-name-reg', 'email-reg', 'phone-reg'],
    //   taglineWithLocation: '{Location} {Subject} {Type}',  // → "San Francisco SAT Tutoring"
    //   tagline: 'Expert {Subject} {Type}',                  // → "Expert SAT Tutoring"
    //   firstQuestion: 'Who will be taking the SAT?',
    //   assumedGradeLevel: 'High School',
    // },
    
    // Example 2: "Tutors" vs "Tutoring" phrasing
    // 'national-math-tutoring-test-b': {
    //   panels: ['who-needs-help-reg', 'grade-level-cond-self', 'grade-level-cond-child', 'grade-level-cond-other', 'how_soon-reg', 'zipcode-reg', 'school-district-reg', 'partner-school-reg', 'full-name-reg', 'email-reg', 'phone-reg'],
    //   taglineWithLocation: 'Expert {Subject} Tutors for students in {Location}',  // → "Expert Math Tutors for students in Chicago"
    //   tagline: 'Expert {Subject} Tutors',                                         // → "Expert Math Tutors"
    // },
    
    // Example 3: Possessive location phrasing
    // 'national-chemistry-tutoring-test-c': {
    //   panels: ['who-needs-help-reg', 'grade-level-hs-col-cond-self', 'grade-level-hs-col-cond-child', 'grade-level-hs-col-cond-other', 'how_soon-reg', 'zipcode-reg', 'school-district-reg', 'partner-school-reg', 'full-name-reg', 'email-reg', 'phone-reg'],
    //   taglineWithLocation: "{Location}'s Premier {Subject} {Type}",  // → "Los Angeles's Premier Chemistry Tutoring"
    //   tagline: 'Premier {Subject} {Type}',                           // → "Premier Chemistry Tutoring"
    // },
  },
  default: {
    panels: [...FULL_OPTION_FLOW],
    headline: '{Subject} {Type}',
  },
};

// Explicit partial-slug rules (ordered; first match wins)
// Define full configs here, same structure as slug configs
const PARTIAL_SLUG_RULES: Array<{
  name: string;
  contains?: string;
  prefix?: string;
  suffix?: string;
  config: SlugPanelConfig;
}> = [
  {
    name: 'contains-elementary-school',
    contains: 'elementary_school',
    config: ELEMENTARY_K6_CONFIG,
  },
  {
    name: 'contains-kids',
    contains: 'kids',
    config: KIDS_CONFIG,
  },
  {
    name: 'contains-math',
    contains: 'national-math-',
    config: {
      panels: [...FULL_OPTION_FLOW],
    }
  },
  {
    name: 'contains-SAT',
    contains: 'national-sat-',
    config: {
      panels: ['who-needs-help-alt', 'sat-section', 'how_soon-reg','zipcode-reg', 'school-district-reg', 'partner-school-reg', /*'finding-tutor-reg',*/ 'full-name-reg', 'email-reg', 'phone-reg'],
      firstQuestion: 'Who will be taking the SAT?',
      assumedGradeLevel: 'High School',
    }
  },
  {
    name: 'contains-ADHD',
    contains: 'national-adhd-',
    config: {
      panels: ['who-needs-help-reg', 'grade-level-hs-col-cond-self', 'grade-level-hs-col-cond-child', 'grade-level-hs-col-cond-other', 'how_soon-reg', 'zipcode-reg', 'school-district-reg', 'partner-school-reg', /*'finding-tutor-reg',*/ 'full-name-reg', 'email-reg', 'phone-reg'],
    }
  },
  {
    name: 'contains-AP-subject',
    contains: 'national-ap_',
    config: {
      panels: ['who-needs-help-reg', 'how_soon-reg', 'zipcode-reg', 'school-district-reg', 'partner-school-reg', /*'finding-tutor-reg',*/ 'full-name-reg', 'email-reg', 'phone-reg'],
      assumedGradeLevel: 'High School',
    }
  },
  {
    name: 'contains-PSAT',
    contains: 'national-psat-',
    config: {
      panels: ['who-needs-help-alt', 'how_soon-reg', 'zipcode-reg', 'school-district-reg', 'partner-school-reg', /*'finding-tutor-reg',*/ 'full-name-reg', 'email-reg', 'phone-reg'],
      firstQuestion: 'Who will be taking the PSAT?',
      assumedGradeLevel: 'High School',
    }
  },
  {
    name: 'contains-college',
    contains: 'national-college-',
    config: COLLEGE_CONFIG,
  },
  {
    name: 'contains-adult',
    contains: 'national-adult-',
    config: ADULT_CONFIG,
  },
  {
    // Spanish tutoring already skips the IEP question; the classes and prep pages match it.
    name: 'contains-spanish',
    contains: 'spanish-',
    config: {
      panels: [...FULL_OPTION_FLOW_WITHOUT_IEP],
    }
  },
  ...ENTRANCE_EXAM_SUBJECTS.map(subject => ({
    name: `contains-${subject}-entrance`,
    contains: `national-${subject}-`,
    config: entranceExamConfig(subject),
  })),
  ...GRAD_TEST_PREP_SUBJECTS.map(subject => ({
    name: `contains-${subject}-gradtp`,
    contains: `national-${subject}-`,
    config: GRAD_TEST_PREP_NAMED_CONFIG,
  })),
];

// Explicit param-based rules (lowest precedence; ordered)
// Define full configs here, same structure as slug configs
const PARAM_RULES: Array<{
  name: string;
  param: string;
  equals: string;
  config: SlugPanelConfig;
}> = [
  {
    name: 'capaignid-20185898587-languages',
    param: 'capaignid',
    equals: '20185898587',
    config: {
      panels: [...FULL_OPTION_FLOW_WITHOUT_IEP],
    }
  },
  {
    name: 'capaignid-20066491418-languages',
    param: 'capaignid',
    equals: '20066491418',
    config: {
      panels: [...FULL_OPTION_FLOW_WITHOUT_IEP],
    }
  },
  {
    name: 'capaignid-20066491430-languages',
    param: 'capaignid',
    equals: '20066491430',
    config: {
      panels: [...FULL_OPTION_FLOW_WITHOUT_IEP],
    }
  },
  {
    name: 'capaignid-20046919878-k6',
    param: 'capaignid',
    equals: '20046919878',
    config: ELEMENTARY_K6_CONFIG,
  },
  {
    name: 'capaignid-21317373007-k12tp',
    param: 'capaignid',
    equals: '21317373007',
    config: {
      panels: ['who-needs-help-reg', 'how_soon-reg', 'zipcode-reg', 'school-district-reg', 'partner-school-reg', /*'finding-tutor-reg',*/ 'full-name-reg', 'email-reg', 'phone-reg'],
      assumedGradeLevel: 'High School',
    }
  },
  {
    name: 'capaignid-20062884866-k12tp',
    param: 'capaignid',
    equals: '20062884866',
    config: {
      panels: ['who-needs-help-reg', 'how_soon-reg', 'zipcode-reg', 'school-district-reg', 'partner-school-reg', /*'finding-tutor-reg',*/ 'full-name-reg', 'email-reg', 'phone-reg'],
      assumedGradeLevel: 'High School',
    }
  },
  {
    name: 'capaignid-20062904075-gradtp',
    param: 'capaignid',
    equals: '20062904075',
    config: GRAD_TEST_PREP_CONFIG,
  },
  {
    name: 'vtpc-pc-account',
    param: 'vtpc',
    equals: 'pc',
    config: {
      panels: ['when-taking', 'taken-before-subject',  'zipcode-reg-prof', /*'finding-tutor-reg',*/ 'full-name-reg-prof', 'email-reg-prof', 'phone-reg-prof'],
      tagline: 'Varsity Tutors: The Leader in {Subject} {Type}',
    }
  },
  {
    name: 'capaignid-20061940293-professional',
    param: 'capaignid',
    equals: '20061940293',
    config: {
      panels: ['how_soon-reg', 'zipcode-reg', /*'finding-tutor-reg',*/ 'full-name-reg', 'email-reg', 'phone-reg'],
      assumedGradeLevel: 'Other',
    }
  },
  {
    name: 'vtpc-pc-prof-certs',
    param: 'vtpc',
    equals: 'pc',
    config: {
      panels: ['how_soon-reg', 'zipcode-reg', /*'finding-tutor-reg',*/ 'full-name-reg', 'email-reg', 'phone-reg'],
      assumedGradeLevel: 'Other',
    }
  },
  {
    name: 'accountid-profcerts',
    param: 'accountid',
    equals: '{561-040-4786}',
    config: {
      panels: ['when-taking-subject', 'taken-before',  'zipcode-reg', /*'finding-tutor-reg',*/ 'full-name-reg', 'email-reg', 'phone-reg']
    }
  },
];

export function getConfigForSlug(
  slugKey: string,
  searchParams?: Record<string, string | string[] | undefined>
): SlugPanelConfig {
  // Normalize once
  const normalizedKey = (slugKey || '').toLowerCase();

  // Audience routing: elementary/kids slugs always use the K-6 flow,
  // ahead of exact, partial, param, and default resolution.
  if (isElementaryOrKidsSlug(normalizedKey)) {
    return normalizedKey.includes('kids') ? KIDS_CONFIG : ELEMENTARY_K6_CONFIG;
  }

  // 1) Exact slug match takes absolute precedence
  const exact = slugFormConfigs.configurations[slugKey];
  if (exact) {
    return exact;
  }

  // 2) Partial slug rules (ordered by specificity; first match wins)
  for (const rule of PARTIAL_SLUG_RULES) {
    if (rule.contains && normalizedKey.includes(rule.contains)) {
      return rule.config;
    }
    if (rule.prefix && normalizedKey.startsWith(rule.prefix)) {
      return rule.config;
    }
    if (rule.suffix && normalizedKey.endsWith(rule.suffix)) {
      return rule.config;
    }
  }

  // 3) URL param-based mapping (lowest precedence)
  if (searchParams) {
    for (const rule of PARAM_RULES) {
      const valRaw = searchParams[rule.param];
      const val = typeof valRaw === 'string' ? valRaw : Array.isArray(valRaw) ? valRaw[0] : undefined;
      if (val === rule.equals) {
        return rule.config;
      }
    }
  }

  // 4) Fallback to default
  return slugFormConfigs.default;
}
